const AGCO_EVENT_TYPES = ["inventory", "position"]

function getAgcoEventStatus(event, applicability = "expected") {
    if (applicability !== "expected") return applicability
    if (!event || (!event.lastRunAt && !event.lastSuccessAt)) {
        return "no_data"
    }

    if (event.failed) {
        return "failed"
    }

    if (event.stale) {
        return "overdue"
    }

    return "local_success"
}

function formatAgcoStatus(status) {
    const labels = {
        failed: "Failed",
        overdue: "Overdue",
        no_data: "No data",
        local_success: "Local success",
        expected: "Expected",
        not_configured: "Not configured",
        not_applicable: "Not applicable",
    }

    return labels[status] || "No data"
}

function buildAgcoStoreRows(stores = []) {
    return stores.map((store) => {
        const events = store.events || {}
        const applicability = store.applicability || "expected"
        const eventStatuses = AGCO_EVENT_TYPES.reduce((acc, eventType) => {
            acc[eventType] = getAgcoEventStatus(events[eventType], applicability)
            return acc
        }, {})
        const issueCount = applicability === "expected"
            ? Object.values(eventStatuses).filter(status => status !== "local_success").length
            : 0

        return {
            id: store.id,
            warehouseName: store.warehouseName || "Unknown Store",
            healthy: issueCount === 0,
            issueCount,
            applicability,
            applicabilityLabel: formatAgcoStatus(applicability),
            inventoryStatus: eventStatuses.inventory,
            positionStatus: eventStatuses.position,
            inventoryLabel: formatAgcoStatus(eventStatuses.inventory),
            positionLabel: formatAgcoStatus(eventStatuses.position),
            qaSeeded: Object.values(events).some(event => !!(event && event.qaSeeded)),
            events,
            failureReason: AGCO_EVENT_TYPES
                .map(eventType => events[eventType])
                .filter(event => event?.failed && event?.message)
                .sort((a, b) => new Date(b.lastRunAt || 0) - new Date(a.lastRunAt || 0))[0]?.message || null,
            lastAttemptAt: AGCO_EVENT_TYPES
                .map(eventType => events[eventType]?.lastRunAt)
                .filter(Boolean)
                .sort((a, b) => new Date(b) - new Date(a))[0] || null,
        }
    })
}

function summarizeAgcoHealth(stores = []) {
    const rows = buildAgcoStoreRows(stores)
    const summary = {
        rows,
        totalStores: rows.length,
        expectedStores: rows.filter(row => row.applicability === "expected").length,
        notConfiguredStores: rows.filter(row => row.applicability === "not_configured").length,
        notApplicableStores: rows.filter(row => row.applicability === "not_applicable").length,
        problemStores: rows.filter(row => row.applicability === "expected" && !row.healthy).length,
        failedEvents: 0,
        overdueEvents: 0,
        missingEvents: 0,
        okEvents: 0,
        qaSeededStores: rows.filter(row => row.qaSeeded).length,
    }

    rows.forEach((row) => {
        ;[row.inventoryStatus, row.positionStatus].forEach((status) => {
            if (status === "failed") summary.failedEvents += 1
            else if (status === "overdue") summary.overdueEvents += 1
            else if (status === "no_data") summary.missingEvents += 1
            else if (status === "local_success") summary.okEvents += 1
        })
    })

    return summary
}

function findUnknownWarehouse(salesByWarehouse = []) {
    return salesByWarehouse.find((warehouse) => {
        const name = String(warehouse.warehouse_name || "").toLowerCase()
        return warehouse.warehouse_id === "unknown" || name === "unknown warehouse"
    }) || null
}

function findTestProducts(products = []) {
    return products.filter(product => /^test product\b/i.test(product.product_name || ""))
}

function summarizeImportEntries(entries = []) {
    const sortedEntries = entries
        .slice()
        .sort((a, b) => new Date(b.createdAt || b.formattedCreatedAt || 0) - new Date(a.createdAt || a.formattedCreatedAt || 0))

    return {
        totalEntries: sortedEntries.length,
        latestEntry: sortedEntries[0] || null,
        recentEntries: sortedEntries.slice(0, 5),
    }
}

export {
    AGCO_EVENT_TYPES,
    buildAgcoStoreRows,
    findTestProducts,
    findUnknownWarehouse,
    formatAgcoStatus,
    getAgcoEventStatus,
    summarizeAgcoHealth,
    summarizeImportEntries,
}
